import React, { useEffect, useState } from "react";
import {
  Button,
  Divider,
  Empty,
  Typography,
  Table,
  Space,
  Input,
  Tag,
  Switch,
} from "antd";
import {
  CopyOutlined,
  PlusOutlined,
  EditOutlined,
  ExportOutlined,
} from "@ant-design/icons";
import { ApiKey } from "../../../../Entities/ApiKey";
import { OAuthClientWithoutClientSecret } from "../../../../Entities/OAuthClient";
import { useLoader } from "../../../../Context/LoaderContext";
import oAuthClientService from "../../../../Service/o.auth.client.service";
import { openNotification } from "../../../../Components/Notification";
import CreateOAuthClientModal from "./CreateOAuthClientModal";
import dayjs from "dayjs";
import "./index.scss";

interface APIProps {
  apiKeys: ApiKey[];
  setSubTab: React.Dispatch<React.SetStateAction<string>>;
}

const API: React.FC<APIProps> = ({ apiKeys, setSubTab }) => {
  const activeApiKeyIds = apiKeys
    .filter((apiKey) => apiKey.status === "active")
    .map((apiKey) => apiKey._id);

  const [oAuthClients, setOAuthClients] = useState<
    OAuthClientWithoutClientSecret[]
  >([]);

  const { isLoading, setLoading, setLoadingMessage } = useLoader();
  const [showCreateNewClientModal, setShowCreateNewClientModal] =
    useState(false);
  const [selectedClient, setSelectedClient] =
    useState<OAuthClientWithoutClientSecret | null>(null);

  const tableData = oAuthClients.map((key, index) => ({
    key: key._id,
    no: index + 1,
    createdAt: key.createdAt,
    clientId: key.clientId,
    redirectUris: key.redirectUris,
    scopes: key.scopes,
    status: key.status,
    _id: key._id,
  }));

  const handleCopyToClipboard = async (text: string) => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
        openNotification("success", "Copied", "Client ID copied to clipboard");
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = text;
        textArea.style.position = "fixed";
        textArea.style.left = "-999999px";
        textArea.style.top = "-999999px";
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();

        try {
          const successful = document.execCommand("copy");
          if (successful) {
            openNotification(
              "success",
              "Copied",
              "Client ID copied to clipboard"
            );
          } else {
            throw new Error("Copy command failed");
          }
        } catch (err) {
          throw new Error("Copy command failed");
        } finally {
          document.body.removeChild(textArea);
        }
      }
    } catch (error) {
      console.error("Failed to copy to clipboard:", error);
      openNotification(
        "error",
        "Failed to copy Client ID",
        "Please copy the ID manually from the input field"
      );
    }
  };

  const handleToggleStatus = async (record: any, checked: boolean) => {
    try {
      setLoading(true);
      setLoadingMessage("Updating OAuth client status...");
      const newStatus = checked ? "active" : "inactive";
      const updatedClient = await oAuthClientService.updateOAuthClient(
        record._id,
        {
          redirectUris: record.redirectUris,
          scopes: record.scopes,
          status: newStatus,
        }
      );
      if (updatedClient) {
        setOAuthClients(
          oAuthClients.map((client) =>
            client._id === record._id
              ? { ...client, status: newStatus }
              : client
          )
        );
        openNotification("success", "Success", "OAuth client status updated");
      }
    } catch (error: any) {
      console.error("Error updating OAuth client status:", error);
      openNotification("error", "Error", error.message);
    } finally {
      setLoading(false);
      setLoadingMessage(null);
    }
  };

  const handleEditClient = (client: OAuthClientWithoutClientSecret) => {
    setSelectedClient(client);
    setShowCreateNewClientModal(true);
  };

  const handleCreateNewClientClick = () => {
    setSelectedClient(null);
    setShowCreateNewClientModal(true);
  };

  const handleDocsClick = () => {
    window.open("/api-docs", "_blank");
  };

  const columns = [
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (text: string, record: any) => {
        const date = dayjs(record.createdAt);
        return (
          <div>
            <div>{date.format("DD-MMM-YYYY")}</div>
            <Typography.Text type="secondary" style={{ fontSize: "12px" }}>
              {date.format("HH:mm A")}
            </Typography.Text>
          </div>
        );
      },
    },
    {
      title: "Client ID",
      dataIndex: "clientId",
      key: "clientId",
      width: 250,
      render: (text: string, record: any) => (
        <Space.Compact style={{ width: "100%" }}>
          <Input defaultValue={text} readOnly id={`client-id-${record._id}`} />
          <Button
            type="primary"
            icon={<CopyOutlined />}
            onClick={() => handleCopyToClipboard(text)}
          />
        </Space.Compact>
      ),
    },
    {
      title: "Scopes",
      dataIndex: "scopes",
      key: "scopes",
      width: 200,
      render: (scopes: string[]) => (
        <Space size={[8, 8]} wrap>
          {scopes.map((scope, index) => (
            <Tag key={index}>{scope}</Tag>
          ))}
        </Space>
      ),
    },
    {
      title: "Redirect URL",
      dataIndex: "redirectUris",
      key: "redirectUris",
      width: 250,
      render: (redirectUris: string[]) => (
        <div>
          {redirectUris.map((uri, index) => (
            <div
              key={index}
              style={{ marginBottom: index < redirectUris.length - 1 ? 4 : 0 }}
            >
              {uri}
            </div>
          ))}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text: string, record: any) => (
        <Tag color={record.status === "active" ? "green" : "red"}>
          {record.status}
        </Tag>
      ),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      render: (text: string, record: any) => (
        <Space>
          <Switch
            checked={record.status === "active"}
            onChange={(checked) => handleToggleStatus(record, checked)}
            checkedChildren="Active"
            unCheckedChildren="Inactive"
          />
          <Button
            type="default"
            icon={<EditOutlined />}
            onClick={() => handleEditClient(record)}
          >
            Edit
          </Button>
        </Space>
      ),
    },
  ];

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      setLoadingMessage("Fetching OAuth clients...");
      const oAuthClients = await oAuthClientService.getOAuthClients();
      setOAuthClients(oAuthClients);
      setLoading(false);
      setLoadingMessage(null);
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="integration-tab-content-section">
      <div className="integration-details-section-header">
        <div className="sub-section-header">
          <Typography.Title level={5} style={{ margin: 0 }}>
            API Integration
          </Typography.Title>

          <Typography.Link onClick={handleDocsClick}>
            Docs <ExportOutlined />
          </Typography.Link>
        </div>

        <Divider style={{ margin: "0" }} />
      </div>

      <section className="integration-tab-content-section-content">
        <Typography.Paragraph>
          <strong>QualifyMe</strong> API Clients are used to authenticate and
          enable integrations with QualifyMe services such as APIs, widgets, and
          web applications.
        </Typography.Paragraph>

        <div className="sub-section-header">
          <Typography.Title level={5} style={{ margin: 0 }}>
            Api Clients
          </Typography.Title>

          {apiKeys.length > 0 && oAuthClients.length > 0 && (
            <Button
              type="primary"
              loading={isLoading}
              icon={<PlusOutlined />}
              onClick={handleCreateNewClientClick}
            >
              New Client
            </Button>
          )}
        </div>

        {apiKeys.length > 0 && (
          <div>
            <Table
              dataSource={tableData}
              columns={columns}
              locale={{
                emptyText: (
                  <Empty
                    description="Looks like you haven't created an API client yet. Click below to create one and get started."
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                  >
                    <Button
                      type="primary"
                      onClick={handleCreateNewClientClick}
                      loading={isLoading}
                      style={{ marginTop: 16 }}
                    >
                      Create Client
                    </Button>
                  </Empty>
                ),
              }}
            />
          </div>
        )}

        {activeApiKeyIds.length === 0 && (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                Looks like you haven't created an API key yet.{" "}
                <Typography.Link
                  onClick={() => setSubTab("api-keys")}
                  style={{ cursor: "pointer" }}
                >
                  Click here to generate
                </Typography.Link>{" "}
                one and get started.
              </span>
            }
          />
        )}
      </section>

      <CreateOAuthClientModal
        showCreateNewClientModal={showCreateNewClientModal}
        setShowCreateNewClientModal={setShowCreateNewClientModal}
        setOAuthClients={setOAuthClients}
        selectedClient={selectedClient}
        setSelectedClient={setSelectedClient}
      />
    </section>
  );
};

export default API;
